import { useQuery } from "@tanstack/react-query";

import React, { useContext, useEffect, useState } from "react";

import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import LaunchIcon from "@mui/icons-material/Launch";
import {
  Box,
  Button,
  LinearProgress,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridFooter,
  GridFooterContainer,
  useGridApiContext,
} from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { useParams } from "react-router-dom";

import { HardwareSensor } from "@safetyculture/s12-apis-connect-web/s12/iot/admin/v1/sensors_pb";
import { Vendor } from "@safetyculture/s12-apis-connect-web/s12/iot/v1/vendor_pb";
import { HardwareSensorReading } from "@safetyculture/s12-apis-connect-web/s12/sensors/v1/hardware_sensor_reading_pb";

import {
  KnownUnits,
  SignalStrengthHelpURL,
  VendorMap,
  readingUnitToSymbolString,
} from "../../../../constants/sensors";
import { convertTimestampToDate } from "../../../../helpers/time";
import { hardwareSensorsOptions } from "../../../../queries/hardware-sensors";
import { UrlParamTypes } from "../../../../types/org-display";
import { getHardwareSensorDetails } from "api/endpoints/sensors";
import { gRPCStatus } from "api/grpc-status-codes";
import { callOptionsHeaders } from "api/helpers";
import {
  AlertStatusChip,
  BatteryStatusChip,
  ConnectionStatusChip,
  SIGNAL_TIER_LABEL,
  SignalStrengthChip,
  signalTier,
} from "components/sensors/table/chips";
import { ColumnHeader } from "components/sensors/table/column-header";
import CopyableCell from "components/sensors/table/copyable-cell";
import {
  DATE_FORMAT,
  copyableDateCellProps,
  localTimezoneLabel,
} from "components/sensors/table/copyable-date";
import { MonoText } from "components/sensors/table/mono-text";
import { NoResultsOverlay } from "components/sensors/table/no-results-overlay";
import {
  NO_VALUE_TEXT_SX,
  noValueText,
} from "components/sensors/table/no-value-text";
import {
  COLUMN_WIDTH,
  CellRow,
  TABLE_HEIGHT,
  dataGridSx,
} from "components/sensors/table/table-layout";
import { GrpcError, SnackbarContext } from "context/snackbar-context";

// RSSI/Battery are device diagnostics, not the sensor's actual measurement --
// sort them after the readings someone's actually here to check.
const deviceDiagnosticReadingNames = new Set(["RSSI", "Battery"]);
const sortReadingsForDisplay = (
  readings: HardwareSensorReading[],
): HardwareSensorReading[] =>
  [...readings].sort((a, b) => {
    const aIsDiagnostic = deviceDiagnosticReadingNames.has(a.name) ? 1 : 0;
    const bIsDiagnostic = deviceDiagnosticReadingNames.has(b.name) ? 1 : 0;
    return aIsDiagnostic - bIsDiagnostic;
  });

// One line of a reading's copy/CSV text, e.g.
// "Temperature_1: 33°C (2026/09/07 14:32:05)".
const readingCopyLine = (reading: HardwareSensorReading): string => {
  const nameAndValue =
    reading.name +
    ": " +
    reading.value +
    (readingUnitToSymbolString.get(reading.unit as KnownUnits) ?? "");
  if (!reading.latestReadingTimestamp) {
    return nameAndValue;
  }
  const absolute = DateTime.fromJSDate(
    convertTimestampToDate(reading.latestReadingTimestamp),
  ).toFormat(DATE_FORMAT);
  return `${nameAndValue} (${absolute})`;
};

const displayID = (row: HardwareSensor): string => {
  if (row.displayId.length > 0) {
    return row.displayId;
  }
  return row.vendorId;
};

// Blank instead of falling back to Source ID -- showing the same value twice
// under different headers just duplicates the Source ID column.
const displayIDColumnValue = (row: HardwareSensor): string => row.displayId;

// Delimited so sensorId/vendorId can't collide across rows (e.g. "ab"+"c"
// vs "a"+"bc" would otherwise produce the same key).
const latestReadingsKey = (row: HardwareSensor): string =>
  `${row.sensorId}:${row.vendorId}`;

// Downloads the CSV directly on click, skipping the extra menu step the
// default GridToolbarExport shows for a single format.
function ExportFooter() {
  const apiRef = useGridApiContext();
  return (
    <GridFooterContainer>
      <Button
        size="small"
        startIcon={<FileDownloadOutlinedIcon />}
        onClick={() => apiRef.current.exportDataAsCsv()}
        sx={{ marginLeft: 1 }}
      >
        Export CSV
      </Button>
      <GridFooter sx={{ border: "none" }} />
    </GridFooterContainer>
  );
}

const OrganisationSensorsTable = () => {
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const [latestReadings, setLatestReadings] = useState<
    Map<string, HardwareSensorReading[]>
  >(new Map());
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [pageSize, setPageSize] = React.useState<number>(100);
  const [page, setPage] = React.useState<number>(0);
  // 100 rows is the maximum number of rows that can be rendered for MIT version of Data Grid
  const { showError } = useContext(SnackbarContext);
  // Separate from the main list's isLoading -- fetching one row's readings
  // shouldn't flip the whole grid into its loading overlay.
  const [isFetchingReadings, setIsFetchingReadings] = useState(false);

  const { data, isLoading, error } = useQuery(
    hardwareSensorsOptions({ regionCode, roleId }),
  );
  const sensorsList: HardwareSensor[] = data?.sensors ?? [];

  useEffect(() => {
    if (error) {
      showError(error as GrpcError);
    }
  }, [error, showError]);

  const displayNotification = (message: string) => {
    setSnackbarMessage(message);
    setOpenSnackbar(true);
  };

  const getLatestReadings = async (row: HardwareSensor) => {
    const sourceName = VendorMap[row.vendor];
    if (!sourceName) {
      showError({
        name: "Error",
        message: "Unable to fetch readings: unknown vendor.",
        code: gRPCStatus.INVALID_ARGUMENT,
      });
      return;
    }
    setIsFetchingReadings(true);
    return getHardwareSensorDetails(
      {
        sourceName,
        sourceId: row.vendorId,
        orgId: roleId,
      },
      callOptionsHeaders(regionCode, roleId),
    )
      .then((deviceReadings) => {
        if (deviceReadings.latestReadings.length === 0) {
          displayNotification(
            "No readings available for device " + displayID(row),
          );
        }
        // New Map instance -- mutating and returning the same reference
        // would let React bail out of re-rendering.
        setLatestReadings((currentReadings) =>
          new Map(currentReadings).set(
            latestReadingsKey(row),
            deviceReadings.latestReadings,
          ),
        );
        setIsFetchingReadings(false);
      })
      .catch((error) => {
        showError(error);
        setIsFetchingReadings(false);
      });
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      width: COLUMN_WIDTH.name,
      renderCell({ row, tabIndex }) {
        return <CopyableCell value={row.name} tabIndex={tabIndex} />;
      },
    },
    {
      field: "location",
      headerName: "Location",
      width: COLUMN_WIDTH.location,
      renderCell({ row, tabIndex }) {
        return <CopyableCell value={row.location} tabIndex={tabIndex} />;
      },
    },
    {
      field: "online",
      headerName: "Status",
      width: 140,
      align: "left",
      valueFormatter: ({ value }) => {
        return value ? "Connected" : "Disconnected";
      },
      renderHeader: () => (
        <ColumnHeader label="Status" tooltip="Connection status" />
      ),
      renderCell({ row }) {
        return (
          <CellRow>
            <ConnectionStatusChip connected={row.online} />
          </CellRow>
        );
      },
    },
    {
      field: "readingTime",
      headerName: `Last seen (${localTimezoneLabel()})`,
      width: 180,
      sortable: false,
      align: "left",
      renderHeader: () => (
        <ColumnHeader
          label="Last seen"
          tooltip={`Times shown in ${localTimezoneLabel()}`}
        />
      ),
      valueGetter({ row }) {
        return row.latestReadingTimestamp
          ? DateTime.fromJSDate(
              convertTimestampToDate(row.latestReadingTimestamp),
            ).toFormat(DATE_FORMAT)
          : "";
      },
      renderCell({ row, tabIndex }) {
        const readingDate = row.latestReadingTimestamp
          ? DateTime.fromJSDate(
              convertTimestampToDate(row.latestReadingTimestamp),
            )
          : null;
        return (
          <CellRow>
            <CopyableCell
              {...copyableDateCellProps(readingDate)}
              tabIndex={tabIndex}
            />
          </CellRow>
        );
      },
    },
    {
      field: "signalStrength",
      headerName: "Signal",
      width: 160,
      align: "left",
      valueGetter({ row }) {
        return signalTier(row.signalStrength, VendorMap[row.vendor as Vendor])
          .rank;
      },
      // CSV export reads valueGetter's value directly (the sortable tier
      // rank), not renderCell's -- format it back to the label shown on
      // screen so the export isn't a bare number.
      valueFormatter: ({ value }) => SIGNAL_TIER_LABEL[value] ?? "",
      renderHeader: () => (
        <Box
          display="flex"
          alignItems="center"
          fontWeight={600}
          fontSize="0.8125rem"
        >
          Signal
          <a href={SignalStrengthHelpURL} target={"_blank"} rel={"noreferrer"}>
            <LaunchIcon
              sx={{
                width: "18px",
                marginLeft: "0.3rem",
                verticalAlign: "middle",
                color: "text.secondary",
              }}
            />
          </a>
        </Box>
      ),
      renderCell: ({ row }) => (
        <SignalStrengthChip
          signalStrength={row.signalStrength}
          vendor={VendorMap[row.vendor as Vendor]}
        />
      ),
    },
    {
      field: "batteryLevel",
      headerName: "Battery",
      width: 110,
      align: "left",
      valueFormatter: ({ value }) => (value ? "Low battery" : "OK"),
      valueGetter: ({ row }) => row.lowBattery,
      renderHeader: () => (
        <ColumnHeader label="Battery" tooltip="Battery level" />
      ),
      renderCell({ row }) {
        return <BatteryStatusChip lowBattery={row.lowBattery} />;
      },
    },
    {
      field: "latestReadingTimestamp",
      headerName: "Latest readings",
      width: 280,
      // Not fetched for every row up front, so there's nothing to sort by
      // until the user clicks Fetch.
      sortable: false,
      align: "left",
      valueGetter({ row }) {
        const rowLatestReadings = latestReadings.get(latestReadingsKey(row));
        const rowHasReadings =
          !!rowLatestReadings && rowLatestReadings.length > 0;

        // Blank (not "Fetch") when there's nothing to show yet -- "Fetch" is
        // the on-screen button label, not a real value, so it shouldn't leak
        // into the CSV export, which reads this valueGetter directly.
        return rowHasReadings
          ? sortReadingsForDisplay(rowLatestReadings)
              .map((reading) => readingCopyLine(reading))
              .join("\n")
          : "";
      },
      renderCell({ row, tabIndex }) {
        const rowLatestReadings = latestReadings.get(latestReadingsKey(row));
        const rowHasReadings =
          !!rowLatestReadings && rowLatestReadings.length > 0;
        const sortedReadings = rowHasReadings
          ? sortReadingsForDisplay(rowLatestReadings)
          : [];

        return rowHasReadings ? (
          <CopyableCell
            value={sortedReadings
              .map((reading) => readingCopyLine(reading))
              .join("\n")}
            tabIndex={tabIndex}
          >
            <Box
              whiteSpace="normal"
              display="flex"
              flexDirection="column"
              textAlign="left"
            >
              {sortedReadings.map((reading) => {
                const readingDate = reading.latestReadingTimestamp
                  ? DateTime.fromJSDate(
                      convertTimestampToDate(reading.latestReadingTimestamp),
                    )
                  : null;
                return (
                  <Box
                    key={`${latestReadingsKey(row)}:${reading.name}`}
                    whiteSpace="normal"
                    display="flex"
                    justifyContent="space-between"
                    alignItems="baseline"
                    gap={1}
                  >
                    <Typography fontSize={14}>
                      {reading.name +
                        ": " +
                        reading.value +
                        (readingUnitToSymbolString.get(
                          reading.unit as KnownUnits,
                        ) ?? "")}
                    </Typography>
                    <Tooltip
                      title={
                        readingDate ? readingDate.toFormat(DATE_FORMAT) : ""
                      }
                    >
                      <Box
                        fontSize={11}
                        color="text.secondary"
                        whiteSpace="nowrap"
                      >
                        {readingDate ? readingDate.toRelative() : "No readings"}
                      </Box>
                    </Tooltip>
                  </Box>
                );
              })}
            </Box>
          </CopyableCell>
        ) : (
          <Button
            variant="outlined"
            size="small"
            color="primary"
            disabled={isFetchingReadings}
            sx={{
              fontSize: "0.6875rem",
              minWidth: 0,
              padding: "2px 10px",
            }}
            onClick={() => {
              getLatestReadings(row);
            }}
            data-testid="btn-get-latest-readings"
          >
            Fetch
          </Button>
        );
      },
    },
    {
      field: "liveMetricAlert",
      headerName: "Alert status",
      width: 150,
      align: "left",
      renderCell: ({ row }) => (
        <AlertStatusChip inAlert={row.liveMetricAlert} />
      ),
      valueFormatter: ({ value }) => (value ? "In alert" : "No alerts"),
    },
    {
      field: "vendor",
      headerName: "Source name",
      width: COLUMN_WIDTH.sourceName,
      align: "left",
      valueGetter: ({ row }) => VendorMap[row.vendor as Vendor] ?? "Unknown",
      // CSV export reads valueGetter's value directly, not renderCell's --
      // keep it in sync with the lowercased display value below.
      valueFormatter: ({ value }) =>
        value === "Unknown" ? value : value.toLowerCase(),
      renderCell: ({ value: vendorName, tabIndex }) => {
        if (vendorName === "Unknown") {
          return (
            <Box component="span" sx={NO_VALUE_TEXT_SX}>
              Unknown
            </Box>
          );
        }
        // VendorMap's values double as SignalStrengthMap lookup keys
        // elsewhere, so they can't be lowercased at the source -- only the
        // displayed label is adjusted here, to plain lowercase.
        return (
          <CopyableCell value={vendorName.toLowerCase()} tabIndex={tabIndex}>
            <MonoText>{vendorName.toLowerCase()}</MonoText>
          </CopyableCell>
        );
      },
    },
    {
      field: "sourceId",
      headerName: "Source ID",
      width: COLUMN_WIDTH.id,
      align: "left",
      valueGetter: ({ row }) => row.vendorId,
      renderCell: ({ row, tabIndex }) => (
        <CopyableCell value={row.vendorId} tabIndex={tabIndex}>
          <MonoText>{row.vendorId}</MonoText>
        </CopyableCell>
      ),
    },
    {
      field: "displayId",
      headerName: "Display ID",
      width: COLUMN_WIDTH.id,
      renderHeader: () => (
        <ColumnHeader
          label="Display ID"
          tooltip="Blank means the same as Source ID"
        />
      ),
      valueGetter: ({ row }) => displayIDColumnValue(row),
      renderCell({ row, tabIndex }) {
        const id = displayIDColumnValue(row);
        return (
          <CopyableCell value={id} tabIndex={tabIndex}>
            {id ? <MonoText>{id}</MonoText> : noValueText("-")}
          </CopyableCell>
        );
      },
    },
    {
      field: "sensorId",
      headerName: "Internal sensor ID",
      width: COLUMN_WIDTH.id,
      align: "left",
      renderCell({ row, tabIndex }) {
        return <CopyableCell value={row.sensorId} tabIndex={tabIndex} />;
      },
    },
  ];

  return (
    <>
      <Box sx={{ height: TABLE_HEIGHT, width: "100%" }}>
        <DataGrid
          rows={sensorsList}
          columns={columns}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          pageSizeOptions={[25, 50, 100]}
          getRowId={(row) => row.displayId || row.vendorId}
          getRowHeight={() => "auto"}
          disableRowSelectionOnClick
          components={{
            LoadingOverlay: LinearProgress,
            NoRowsOverlay: isLoading ? LinearProgress : NoResultsOverlay,
            Footer: ExportFooter,
          }}
          data-testid="org-sensor-table"
          disableVirtualization
          loading={isLoading}
          sortingOrder={["asc", "desc"]}
          initialState={{
            columns: {
              columnVisibilityModel: {
                sensorId: false,
              },
            },
            sorting: {
              sortModel: [{ field: "location", sort: "asc" }],
            },
            pagination: {
              paginationModel: { pageSize: 100 },
            },
          }}
          sx={dataGridSx}
        />
      </Box>
      <Snackbar
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        message={snackbarMessage}
      />
    </>
  );
};

export default OrganisationSensorsTable;
